import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import axios from 'axios';
import { BackupExportRestore } from './superAdmin/BackupExportRestore';
import { SeasonRecapCard } from './superAdmin/SeasonRecapCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TicketASaurus logo URL
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_teamtix-manager/artifacts/7dt7doo8_TicketASaurus.png';

export const SuperAdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true); // Start loading while checking session
  const [showPassword, setShowPassword] = useState(false);
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  
  // Data states
  const [partners, setPartners] = useState([]);
  const [games, setGames] = useState([]);
  const [partnerAccess, setPartnerAccess] = useState({});
  const [selectedPartner, setSelectedPartner] = useState('F');
  const [accessLogs, setAccessLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'events', 'access', 'logs', 'settings', or 'communications'
  
  // Settings state
  const [settings, setSettings] = useState({
    app_title: 'Mavs Season Tickets',
    footer_text: '',
    season_year: '2025-26',
    ticker_speed: 40,
    ticker_enabled: true,
    show_next_game: true,
    show_standings: true,
    show_recent_games: true,
    show_player_stats: true,
    finley_name: 'Finley',
    bill_name: 'Bill',
    regular_total_games: 0,
    playoff_total_games: 0,
    total_seats: 6,
    total_parking: 2,
    finley_amount_paid: 0,
    bill_amount_paid: 0,
    finley_playoff_paid: 0,
    bill_playoff_paid: 0,
    playoff_enabled: false,
    // SMTP Settings
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_use_tls: true,
    // Request Access Settings
    request_access_email: '',
    // Backup Email Settings
    backup_email_enabled: false,
    backup_email_address: '',
    backup_email_day: 'sunday',
    backup_email_time: '06:00'
  });
  const [settingsMessage, setSettingsMessage] = useState('');
  const [smtpMessage, setSmtpMessage] = useState('');
  const [backupMessage, setBackupMessage] = useState('');
  const [backupEmailMessage, setBackupEmailMessage] = useState('');
  const [sendingBackupEmail, setSendingBackupEmail] = useState(false);
  const [gamesMessage, setGamesMessage] = useState('');
  const [ticketAssignments, setTicketAssignments] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  
  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    partner_id: '',
    role: 'partner'
  });

  // New event/game form
  const [newEvent, setNewEvent] = useState({
    opponent: '',
    date: '',
    timeCT: '7:30 PM',
    season_type: 'regular',
    note: ''
  });

  // Events/Seasons state - loaded from API
  const [events, setEvents] = useState([]);
  const [showArchivedEvents, setShowArchivedEvents] = useState(false);

  // New event form for creating events
  const [newEventForm, setNewEventForm] = useState({
    name: '',
    sport: 'basketball',
    season: '',
    seat_count: 6,
    parking_count: 2
  });

  // User assignment modal
  const [showUserAssignModal, setShowUserAssignModal] = useState(false);
  const [selectedEventForAssignment, setSelectedEventForAssignment] = useState(null);

  // Edit user modal
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserForm, setEditUserForm] = useState({
    originalEmail: '',
    email: '',
    name: '',
    partner_id: '',
    role: 'partner'
  });

  // Communications state
  const [emailForm, setEmailForm] = useState({
    recipients: 'all',
    subject: '',
    message: ''
  });
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailStats, setEmailStats] = useState({ total_campaigns: 0, total_emails_sent: 0 });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  
  // Automation settings for email reminders
  const [automationSettings, setAutomationSettings] = useState({
    reminder_24h_enabled: false,
    reminder_24h_time: '09:00',
    reminder_week_enabled: false,
    reminder_week_day: 'monday',
    reminder_week_time: '09:00'
  });
  const [savingAutomation, setSavingAutomation] = useState(false);

  // Email preview state
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [previewTemplateType, setPreviewTemplateType] = useState('daily');
  const [previewPreviews, setPreviewPreviews] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewFilterUser, setPreviewFilterUser] = useState('all');

  const handleOpenEmailPreview = async (templateType) => {
    setPreviewTemplateType(templateType);
    setShowEmailPreview(true);
    setPreviewLoading(true);
    setPreviewIndex(0);
    setPreviewFilterUser('all');
    try {
      const res = await axios.get(`${API}/superadmin/email-preview`, {
        params: { template_type: templateType }
      });
      if (res.data.success) {
        setPreviewPreviews(res.data.previews || []);
      } else {
        setPreviewPreviews([]);
      }
    } catch {
      setPreviewPreviews([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const filteredPreviews = previewFilterUser === 'all' 
    ? previewPreviews 
    : previewPreviews.filter(p => p.user_email === previewFilterUser);

  // Edit event state
  const [editingEvent, setEditingEvent] = useState(null);
  const [editEventForm, setEditEventForm] = useState({
    name: '',
    sport: 'basketball',
    season: '',
    status: 'active',
    seat_count: 6,
    parking_count: 2,
    primaryColor: '#0064B1',
    secondaryColor: '#111111'
  });
  
  // Track which event's user dropdown is open
  const [openUserDropdown, setOpenUserDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openUserDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenUserDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openUserDropdown]);

  // Load events from API
  const loadEvents = async () => {
    try {
      const response = await axios.get(`${API}/events/`);
      if (response.data.success) {
        // Transform backend format to frontend format
        const transformedEvents = response.data.events.map(e => ({
          id: e.id,
          name: e.name,
          sport: e.sport,
          season: e.season,
          status: e.status,
          gamesCount: e.games_count || 0,
          assignedUsers: e.assigned_users || [],
          adminArchived: e.admin_archived || false,
          primaryColor: e.primary_color,
          secondaryColor: e.secondary_color
        }));
        setEvents(transformedEvents);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    // Set TicketASaurus favicon for SuperAdmin page
    const favicon = document.getElementById('dynamic-favicon') || document.querySelector('link[rel="icon"]');
    if (favicon) favicon.href = '/ticketasaurus-logo.png';
    document.title = 'TicketASaurus | Super Admin';
    
    const storedUser = sessionStorage.getItem('superadmin_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user.role === 'superadmin') {
          setIsAuthenticated(true);
          loadData();
          loadEvents();
        }
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }
    setIsLoading(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      // Try user login first
      const response = await axios.post(`${API}/users/login`, {
        email: email.trim().toLowerCase(),
        password
      });
      
      if (response.data.success && response.data.user.role === 'superadmin') {
        // Store session
        sessionStorage.setItem('superadmin_user', JSON.stringify(response.data.user));
        setIsAuthenticated(true);
        loadData();
        loadEvents();
      } else {
        setError('Access denied. Super admin only.');
      }
    } catch (err) {
      setError('Invalid credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('superadmin_user');
    setIsAuthenticated(false);
    // Redirect to main login page
    window.location.href = '/';
  };

  const loadData = async () => {
    try {
      const [partnersRes, accessRes, logsRes, usersRes, settingsRes, ticketsRes, activityRes, globalSettingsRes, emailHistoryRes, automationRes] = await Promise.all([
        axios.get(`${API}/superadmin/partners`),
        axios.get(`${API}/superadmin/partner-access`),
        axios.get(`${API}/superadmin/access-logs`),
        axios.get(`${API}/users/`),
        axios.get(`${API}/settings/`),
        axios.get(`${API}/tickets/assignments`),
        axios.get(`${API}/superadmin/activity-logs`),
        axios.get(`${API}/superadmin/global-settings`),
        axios.get(`${API}/superadmin/email-history`),
        axios.get(`${API}/superadmin/email-automation`).catch(() => ({ data: { success: false } }))
      ]);
      
      if (partnersRes.data.success) {
        setPartners(partnersRes.data.partners);
      }
      
      if (accessRes.data.success) {
        setPartnerAccess(accessRes.data.access);
        setGames(accessRes.data.games);
      }
      
      if (logsRes.data.success) {
        setAccessLogs(logsRes.data.logs);
      }
      
      if (activityRes.data.success) {
        setActivityLogs(activityRes.data.logs);
      }
      
      if (usersRes.data.success) {
        setUsers(usersRes.data.users);
      }
      
      if (emailHistoryRes.data.success) {
        setEmailHistory(emailHistoryRes.data.emails || []);
        setEmailStats({
          total_campaigns: emailHistoryRes.data.total_campaigns || 0,
          total_emails_sent: emailHistoryRes.data.total_emails_sent || 0
        });
      }
      
      // Load automation settings
      if (automationRes.data.success) {
        setAutomationSettings(automationRes.data.settings || {
          reminder_24h_enabled: false,
          reminder_24h_time: '09:00',
          reminder_week_enabled: false,
          reminder_week_day: 'monday',
          reminder_week_time: '09:00'
        });
      }
      
      // Load global settings (footer + SMTP + backup email) separately from event settings
      if (globalSettingsRes.data.success) {
        const globalSettings = globalSettingsRes.data.settings;
        setSettings(prev => ({
          ...prev,
          ...settingsRes.data?.settings,
          footer_text: globalSettings.footer_text || '',
          smtp_host: globalSettings.smtp_host || '',
          smtp_port: globalSettings.smtp_port || 587,
          smtp_username: globalSettings.smtp_username || '',
          smtp_password: globalSettings.smtp_password || '',
          smtp_from_email: globalSettings.smtp_from_email || '',
          smtp_from_name: globalSettings.smtp_from_name || '',
          smtp_use_tls: globalSettings.smtp_use_tls !== false,
          // Request Access settings
          request_access_email: globalSettings.request_access_email || '',
          // Backup email settings
          backup_email_enabled: globalSettings.backup_email_enabled || false,
          backup_email_address: globalSettings.backup_email_address || '',
          backup_email_day: globalSettings.backup_email_day || 'sunday',
          backup_email_time: globalSettings.backup_email_time || '06:00'
        }));
      } else if (settingsRes.data.success) {
        setSettings(settingsRes.data.settings);
      }
      
      if (ticketsRes.data.success) {
        setTicketAssignments(ticketsRes.data.assignments || {});
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validate partner_id is not duplicate
    const existingPartnerIds = users.filter(u => u.partner_id).map(u => u.partner_id.toUpperCase());
    if (newUser.partner_id && existingPartnerIds.includes(newUser.partner_id.toUpperCase())) {
      setUserMessage(`❌ Partner ID "${newUser.partner_id}" already exists. Please use a different ID.`);
      return;
    }
    
    // Validate required fields
    if (!newUser.name || !newUser.email || !newUser.password) {
      setUserMessage('❌ Please fill in all required fields.');
      return;
    }
    
    try {
      setUserMessage('Creating user...');
      await axios.post(`${API}/users/`, newUser);
      setUserMessage('✅ User created successfully!');
      setNewUser({ email: '', password: '', name: '', partner_id: '', role: 'partner' });
      loadData();
      setTimeout(() => setUserMessage(''), 3000);
    } catch (err) {
      setUserMessage(`❌ ${err.response?.data?.detail || 'Error creating user'}`);
    }
  };

  const [userMessage, setUserMessage] = useState('');

  const handleUpdateUserPassword = async (userEmail, newPassword) => {
    try {
      await axios.put(`${API}/users/${userEmail}`, { password: newPassword });
      alert('Password updated successfully');
    } catch (err) {
      alert('Error updating password');
    }
  };

  const handleToggleUserActive = async (userEmail, isActive) => {
    try {
      await axios.put(`${API}/users/${userEmail}`, { is_active: !isActive });
      loadData();
    } catch (err) {
      alert('Error updating user');
    }
  };

  const handleToggleEventManager = async (userEmail, currentValue) => {
    try {
      await axios.put(`${API}/users/${userEmail}`, { is_event_manager: !currentValue });
      loadData();
    } catch (err) {
      alert('Error updating user');
    }
  };

  const handleToggleReceivesEmails = async (userEmail, currentValue) => {
    try {
      await axios.put(`${API}/users/${userEmail}`, { receives_emails: !currentValue });
      loadData();
    } catch (err) {
      alert('Error updating user');
    }
  };

  const handleToggleFEDV = async (userEmail) => {
    try {
      await axios.post(`${API}/users/${userEmail}/toggle-fedv`);
      loadData();
      loadEvents();
    } catch (err) {
      alert('Error toggling FE/DV');
    }
  };

  const handleToggleTC = async (userEmail) => {
    try {
      await axios.post(`${API}/users/${userEmail}/toggle-tc`);
      loadData();
      loadEvents();
    } catch (err) {
      alert('Error toggling T/C');
    }
  };

  const handleEditUser = (user) => {
    setEditUserForm({
      originalEmail: user.email,
      email: user.email,
      name: user.name,
      partner_id: user.partner_id || '',
      role: user.role || 'partner'
    });
    setShowEditUserModal(true);
  };

  const handleSaveUserEdit = async () => {
    try {
      const updateData = {
        name: editUserForm.name,
        partner_id: editUserForm.partner_id
      };
      
      // Only update email if it changed
      if (editUserForm.email !== editUserForm.originalEmail) {
        updateData.email = editUserForm.email;
      }
      
      await axios.put(`${API}/users/${editUserForm.originalEmail}`, updateData);
      setShowEditUserModal(false);
      loadData();
      alert('✅ User updated successfully!');
    } catch (err) {
      alert(`❌ ${err.response?.data?.detail || 'Error updating user'}`);
    }
  };

  const handleEmulateUser = (user) => {
    if (user.role === 'superadmin') {
      alert('Cannot emulate superadmin account');
      return;
    }
    
    // Confirm emulation
    if (!window.confirm(`Emulate user "${user.name}" (${user.email})?\n\nYou will be logged in as this user to see what they see.`)) {
      return;
    }
    
    // Store emulation data in sessionStorage
    sessionStorage.setItem('emulatedUser', JSON.stringify({
      email: user.email,
      name: user.name,
      partner_id: user.partner_id,
      is_event_manager: user.is_event_manager,
      emulatedBy: 'superadmin',
      emulatedAt: new Date().toISOString()
    }));
    
    // Also store in localStorage for the main app to pick up
    localStorage.setItem('isEmulating', 'true');
    localStorage.setItem('emulatedUserEmail', user.email);
    localStorage.setItem('emulatedUserName', user.name);
    localStorage.setItem('emulatedUserPartnerId', user.partner_id || '');
    localStorage.setItem('emulatedUserIsEventManager', user.is_event_manager ? 'true' : 'false');
    
    // Log the emulation activity
    axios.post(`${API}/superadmin/log-activity`, {
      action: 'user_emulation',
      details: `SuperAdmin started emulating user: ${user.name} (${user.email})`,
      user_email: 'superadmin@admin.com',
      user_name: 'SuperAdmin'
    }).catch(() => {});
    
    // Clear current event so the emulated user gets their event selection
    localStorage.removeItem('currentEvent');
    localStorage.setItem('isAuthenticated', 'true');
    
    // Store user in sessionStorage for CoverPage to pick up
    sessionStorage.setItem('loggedInUser', JSON.stringify({
      email: user.email,
      name: user.name,
      partner_id: user.partner_id,
      is_event_manager: user.is_event_manager
    }));
    
    // Set flag to go directly to event selection
    sessionStorage.setItem('switchingEvents', 'true');
    
    // Redirect to main app - will show event selection for emulated user
    window.location.href = '/';
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.message) {
      setEmailMessage('❌ Please fill in subject and message');
      return;
    }

    setSendingEmail(true);
    setEmailMessage('');

    try {
      // Determine recipients based on selection
      let recipientEmails = [];
      
      if (emailForm.recipients === 'all') {
        recipientEmails = users
          .filter(u => u.role !== 'superadmin' && u.is_active !== false && u.receives_emails !== false)
          .map(u => u.email);
      } else if (emailForm.recipients === 'partners') {
        recipientEmails = users
          .filter(u => u.role === 'partner' && u.is_active !== false && u.receives_emails !== false)
          .map(u => u.email);
      } else {
        // Single user email - check if they have emails enabled
        const selectedUser = users.find(u => u.email === emailForm.recipients);
        if (selectedUser && selectedUser.receives_emails === false) {
          setEmailMessage(`❌ ${selectedUser.name} has emails disabled. Enable emails in Users panel first.`);
          setSendingEmail(false);
          return;
        }
        recipientEmails = [emailForm.recipients];
      }

      if (recipientEmails.length === 0) {
        setEmailMessage('❌ No recipients selected or all recipients have emails disabled');
        setSendingEmail(false);
        return;
      }

      // Show confirmation with recipient count
      const confirmMsg = `Send email to ${recipientEmails.length} recipient(s)?\n\nRecipients:\n${recipientEmails.slice(0, 5).join('\n')}${recipientEmails.length > 5 ? '\n...' : ''}`;
      if (!window.confirm(confirmMsg)) {
        setSendingEmail(false);
        return;
      }

      const response = await axios.post(`${API}/superadmin/send-email`, {
        recipients: recipientEmails,
        subject: emailForm.subject,
        message: emailForm.message
      });

      if (response.data.success) {
        setEmailMessage(`✅ ${response.data.message}`);
        setEmailForm({ recipients: 'all', subject: '', message: '' });
        loadData(); // Refresh email history
      } else {
        setEmailMessage(`❌ ${response.data.error}`);
      }
    } catch (err) {
      setEmailMessage(`❌ ${err.response?.data?.error || 'Error sending email'}`);
    } finally {
      setSendingEmail(false);
      setTimeout(() => setEmailMessage(''), 5000);
    }
  };

  // Ref to track if an email send is in progress (prevents race conditions)
  const emailSendingRef = React.useRef(false);
  
  const handleSendGameReminder = async () => {
    // Double-check with ref to prevent duplicate calls
    if (emailSendingRef.current || sendingEmail) {
      console.log('Email send already in progress, ignoring duplicate call');
      return;
    }
    emailSendingRef.current = true;
    setSendingEmail(true);
    setEmailMessage('');

    try {
      // First check if there are games in next 24 hours
      const gamesCheck = await axios.get(`${API}/superadmin/upcoming-games`);
      
      if (!gamesCheck.data.success || gamesCheck.data.count === 0) {
        setEmailMessage('❌ No games found in the next 24 hours');
        return;
      }

      const gamesList = gamesCheck.data.games.map(g => `• ${g.opponent} - ${g.date}`).join('\n');
      const confirmMsg = `Send game reminder for ${gamesCheck.data.count} game(s) in the next 24 hours?\n\n${gamesList}\n\nThis will email all users with emails enabled.`;
      
      if (!window.confirm(confirmMsg)) {
        return;
      }

      const response = await axios.post(`${API}/superadmin/send-game-reminder`, { force_send: true });

      if (response.data.success) {
        setEmailMessage(`✅ ${response.data.message}`);
        loadData();
      } else {
        setEmailMessage(`❌ ${response.data.error}`);
      }
    } catch (err) {
      setEmailMessage(`❌ ${err.response?.data?.error || 'Error sending game reminder'}`);
    } finally {
      setSendingEmail(false);
      emailSendingRef.current = false;
      setTimeout(() => setEmailMessage(''), 8000);
    }
  };

  const handleSendWeeklyGameReminder = async () => {
    // Double-check with ref to prevent duplicate calls
    if (emailSendingRef.current || sendingEmail) {
      console.log('Email send already in progress, ignoring duplicate call');
      return;
    }
    emailSendingRef.current = true;
    setSendingEmail(true);
    setEmailMessage('');

    try {
      // First check if there are games in next 7 days
      const gamesCheck = await axios.get(`${API}/superadmin/upcoming-games-week`);
      
      if (!gamesCheck.data.success || gamesCheck.data.count === 0) {
        setEmailMessage('❌ No games found in the next 7 days');
        return;
      }

      const gamesList = gamesCheck.data.games.map(g => {
        const eventName = g.event_name ? g.event_name.split('(')[0].trim() : 'Unknown';
        return `• ${eventName} vs ${g.opponent} - ${g.date}`;
      }).join('\n');
      const confirmMsg = `Send weekly game reminder for ${gamesCheck.data.count} game(s) in the next 7 days?\n\n${gamesList}\n\nThis will email all users with emails enabled.`;
      
      if (!window.confirm(confirmMsg)) {
        return;
      }

      const response = await axios.post(`${API}/superadmin/send-game-reminder-week`, { force_send: true });

      if (response.data.success) {
        setEmailMessage(`✅ ${response.data.message}`);
        loadData();
      } else {
        setEmailMessage(`❌ ${response.data.error}`);
      }
    } catch (err) {
      setEmailMessage(`❌ ${err.response?.data?.error || 'Error sending weekly game reminder'}`);
    } finally {
      setSendingEmail(false);
      emailSendingRef.current = false;
      setTimeout(() => setEmailMessage(''), 8000);
    }
  };

  const handleClearEmailHistory = async () => {
    if (!window.confirm('Are you sure you want to delete all email history? This cannot be undone.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/superadmin/email-history`);
      if (response.data.success) {
        setEmailMessage(`✅ ${response.data.message}`);
        loadData();
      }
    } catch (err) {
      setEmailMessage(`❌ Error clearing email history`);
    }
  };

  const handleSaveAutomation = async (newSettings) => {
    setSavingAutomation(true);
    try {
      const response = await axios.post(`${API}/superadmin/email-automation`, newSettings);
      if (response.data.success) {
        setAutomationSettings(newSettings);
        setEmailMessage(`✅ Automation settings saved`);
        setTimeout(() => setEmailMessage(''), 3000);
      } else {
        setEmailMessage(`❌ ${response.data.error}`);
      }
    } catch (err) {
      setEmailMessage(`❌ Error saving automation settings`);
    } finally {
      setSavingAutomation(false);
    }
  };

  const handleDeleteUser = async (userEmail, userName) => {
    // First check if user has allocations
    try {
      const allocRes = await axios.get(`${API}/users/${encodeURIComponent(userEmail)}/allocations`);
      const { allocated_tickets, allocated_parking, partner_id } = allocRes.data;
      
      let confirmMessage = `Are you sure you want to delete user "${userName}" (${userEmail})?\n\nThis will deactivate the account.`;
      
      // If user has allocations, warn that they will be cleared
      if (allocated_tickets > 0 || allocated_parking > 0) {
        confirmMessage = `⚠️ Warning: ${userName} (Partner ${partner_id}) has:\n• ${allocated_tickets} tickets allocated\n• ${allocated_parking} parking passes allocated\n\nDeleting this user will automatically CLEAR all their allocations.\n\nAre you sure you want to proceed?`;
      }
      
      // Confirm deletion
      if (!window.confirm(confirmMessage)) {
        return;
      }
      
      const response = await axios.delete(`${API}/users/${encodeURIComponent(userEmail)}`);
      const clearedMsg = response.data.cleared_allocations > 0 
        ? ` Cleared ${response.data.cleared_allocations} allocations.` 
        : '';
      setUserMessage(`✅ User "${userName}" deleted successfully!${clearedMsg}`);
      loadData();
      loadEvents(); // Refresh events to reflect removed user assignments
      setTimeout(() => setUserMessage(''), 5000);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error deleting user';
      alert(`❌ ${errorMsg}`);
    }
  };

  // Save settings (footer goes to global settings)
  const handleSaveSettings = async () => {
    try {
      setSettingsMessage('Saving...');
      // Save footer to global settings
      await axios.post(`${API}/superadmin/global-settings`, {
        footer_text: settings.footer_text || ''
      });
      setSettingsMessage('✅ Settings saved successfully!');
      setTimeout(() => setSettingsMessage(''), 3000);
    } catch (err) {
      setSettingsMessage('❌ Error saving settings');
    }
  };

  // Save SMTP settings
  const handleSaveSmtp = async () => {
    try {
      setSmtpMessage('Saving SMTP settings...');
      await axios.post(`${API}/superadmin/smtp-settings`, {
        smtp_host: settings.smtp_host || '',
        smtp_port: settings.smtp_port || 587,
        smtp_username: settings.smtp_username || '',
        smtp_password: settings.smtp_password || '',
        smtp_from_email: settings.smtp_from_email || '',
        smtp_from_name: settings.smtp_from_name || '',
        smtp_use_tls: settings.smtp_use_tls !== false,
        request_access_email: settings.request_access_email || ''
      });
      setSmtpMessage('✅ SMTP settings saved successfully!');
      setTimeout(() => setSmtpMessage(''), 3000);
    } catch (err) {
      setSmtpMessage('❌ Error saving SMTP settings');
    }
  };

  // Test SMTP connection
  const handleTestSmtp = async () => {
    try {
      setSmtpMessage('Testing SMTP connection...');
      const response = await axios.post(`${API}/superadmin/test-smtp`);
      if (response.data.success) {
        setSmtpMessage('✅ SMTP test successful! Email sent.');
      } else {
        setSmtpMessage(`❌ SMTP test failed: ${response.data.error || 'Unknown error'}`);
      }
      setTimeout(() => setSmtpMessage(''), 5000);
    } catch (err) {
      setSmtpMessage(`❌ SMTP test failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Backup database
  const handleBackup = async () => {
    try {
      setBackupMessage('Creating backup...');
      const response = await axios.get(`${API}/backup/export`, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticketsaurus_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setBackupMessage('✅ Backup downloaded successfully!');
      setTimeout(() => setBackupMessage(''), 5000);
    } catch (err) {
      setBackupMessage('❌ Backup failed. Please try again.');
    }
  };

  // Restore database
  const handleRestore = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!window.confirm('⚠️ This will replace your current database with the backup. Are you sure?')) {
      event.target.value = '';
      return;
    }

    try {
      setBackupMessage('Restoring database...');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/backup/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Check for success - handle both boolean and string responses
      if (response.data && (response.data.success === true || response.data.success === 'true' || response.data.restored_collections)) {
        const collections = response.data.restored_collections || [];
        setBackupMessage(`✅ Database restored successfully! ${collections.length} collections restored.`);
        loadData();
      } else {
        setBackupMessage(`❌ Restore failed: ${response.data?.detail || response.data?.message || 'Unknown error'}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Please check the file format.';
      setBackupMessage(`❌ Restore failed: ${errorMsg}`);
      console.error('Restore error:', err);
    }
    event.target.value = '';
  };

  // Export games to Excel
  const handleExportGames = async () => {
    try {
      setGamesMessage('Exporting games schedule...');
      const response = await axios.get(`${API}/games/export-excel`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `games_schedule_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setGamesMessage('✅ Games schedule exported!');
      setTimeout(() => setGamesMessage(''), 5000);
    } catch (error) {
      setGamesMessage('❌ Export failed. Please try again.');
    }
  };

  // Import games from Excel
  const handleImportGames = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!window.confirm('⚠️ This will replace the current games schedule. Continue?')) {
      event.target.value = '';
      return;
    }

    try {
      setGamesMessage('Importing games schedule...');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/games/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setGamesMessage(`✅ Imported ${response.data.games_count} games!`);
        loadData();
      }
    } catch (error) {
      setGamesMessage('❌ Import failed. Check file format.');
    }
    event.target.value = '';
  };

  // Calculate partner usage cost - uses actual game counts from loaded games
  const calculateUsageCost = (partnerId) => {
    const totalRegularCost = (settings.finley_amount_paid || 0) + (settings.bill_amount_paid || 0);
    // Calculate from actual loaded games instead of settings
    const regularGames = games.filter(g => g.type !== 'playoff' && g.season_type !== 'playoff').length;
    const playoffGames = games.filter(g => g.type === 'playoff' || g.season_type === 'playoff').length;
    const totalSeats = settings.total_seats || 6;
    const regularCostPerTicket = regularGames > 0 && totalSeats > 0 
      ? totalRegularCost / (regularGames * totalSeats) 
      : 0;
    
    const totalPlayoffCost = (settings.finley_playoff_paid || 0) + (settings.bill_playoff_paid || 0);
    const playoffCostPerTicket = playoffGames > 0 && totalSeats > 0 
      ? totalPlayoffCost / (playoffGames * totalSeats) 
      : 0;

    let regularTickets = 0;
    let playoffTickets = 0;

    Object.entries(ticketAssignments).forEach(([gameIdxStr, game]) => {
      if (game.tickets && Array.isArray(game.tickets)) {
        const partnerTickets = game.tickets.filter(t => t === partnerId).length;
        const gameIdx = parseInt(gameIdxStr);
        const actualGame = games.find(g => g.game_index === gameIdx);
        
        if (actualGame) {
          const isPlayoffGame = actualGame.season_type === 'playoff' || actualGame.type === 'playoff';
          if (isPlayoffGame) {
            playoffTickets += partnerTickets;
          } else {
            regularTickets += partnerTickets;
          }
        }
      }
    });

    return (regularTickets * regularCostPerTicket) + (playoffTickets * playoffCostPerTicket);
  };

  // Create new season/event
  const handleCreateSeasonEvent = async () => {
    if (!newEventForm.name || !newEventForm.season) {
      alert('Please fill in Team Name and Season');
      return;
    }

    try {
      const response = await axios.post(`${API}/events/`, {
        name: newEventForm.name,
        sport: newEventForm.sport,
        season: newEventForm.season,
        seat_count: parseInt(newEventForm.seat_count) || 6,
        parking_count: parseInt(newEventForm.parking_count) || 2,
        status: 'active'
      });

      if (response.data.success) {
        setNewEventForm({ name: '', sport: 'basketball', season: '', seat_count: 6, parking_count: 2 });
        loadEvents(); // Reload events from API
        alert(`✅ Event "${newEventForm.name}" created successfully!`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating event');
    }
  };

  // Delete season/event
  const handleToggleEventArchived = async (eventId) => {
    try {
      const res = await axios.patch(`${API}/events/${eventId}/toggle-admin-archived`);
      setEvents(prev => prev.map(ev => ev.id === eventId ? { ...ev, adminArchived: res.data.admin_archived } : ev));
    } catch (error) {
      alert('Failed to update archive status');
    }
  };

  const handleDeleteSeasonEvent = async (eventId) => {
    const eventToDelete = events.find(e => e.id === eventId);
    if (!eventToDelete) return;

    if (!window.confirm(`Are you sure you want to delete "${eventToDelete.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/events/${eventId}`);
      if (response.data.success) {
        loadEvents(); // Reload events from API
        alert(`✅ Event "${eventToDelete.name}" deleted`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting event');
    }
  };

  // Assign user to event
  const handleAssignUserToEvent = async (eventId, userEmail) => {
    try {
      await axios.post(`${API}/events/${eventId}/assign-user?user_email=${encodeURIComponent(userEmail)}`);
      loadEvents(); // Reload events from API
    } catch (err) {
      console.error('Error assigning user:', err);
    }
  };

  // Remove user from event
  const handleRemoveUserFromEvent = async (eventId, userEmail) => {
    try {
      await axios.post(`${API}/events/${eventId}/remove-user?user_email=${encodeURIComponent(userEmail)}`);
      loadEvents(); // Reload events from API
    } catch (err) {
      console.error('Error removing user:', err);
    }
  };

  // Open edit event modal
  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEditEventForm({
      name: event.name,
      sport: event.sport,
      season: event.season,
      status: event.status || 'active',
      seat_count: event.seat_count || 6,
      parking_count: event.parking_count || 2,
      primaryColor: event.primaryColor || '#0064B1',
      secondaryColor: event.secondaryColor || '#111111'
    });
  };

  // Save edited event
  const handleSaveEventEdit = async () => {
    if (!editingEvent) return;
    
    try {
      const response = await axios.put(`${API}/events/${editingEvent.id}`, {
        name: editEventForm.name,
        sport: editEventForm.sport,
        season: editEventForm.season,
        status: editEventForm.status,
        primary_color: editEventForm.primaryColor,
        secondary_color: editEventForm.secondaryColor
      });
      
      if (response.data.success) {
        loadEvents(); // Reload events
        setEditingEvent(null);
        alert('✅ Event updated successfully!');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating event');
    }
  };

  // Get sport emoji
  const getSportEmoji = (sport) => {
    const emojis = {
      basketball: '🏀',
      hockey: '🏒',
      football: '🏈',
      baseball: '⚾',
      concerts: '🎭',
      family: '🏠'
    };
    return emojis[sport] || '🎮';
  };

  // Create new game/event
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/games/`, {
        ...newEvent,
        game_index: games.length // Auto-assign next index
      });
      setNewEvent({ opponent: '', date: '', timeCT: '7:30 PM', season_type: 'regular', note: '' });
      loadData();
      alert('Game created successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating game');
    }
  };

  // Delete game/event
  const handleDeleteEvent = async (gameIndex) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;
    
    try {
      await axios.delete(`${API}/games/${gameIndex}`);
      loadData();
    } catch (err) {
      alert('Error deleting game');
    }
  };

  const toggleGameAccess = async (gameIndex) => {
    const currentAccess = partnerAccess[selectedPartner] || [];
    let newAccess;
    
    if (currentAccess.includes(gameIndex)) {
      newAccess = currentAccess.filter(idx => idx !== gameIndex);
    } else {
      newAccess = [...currentAccess, gameIndex];
    }
    
    // Update locally
    setPartnerAccess(prev => ({
      ...prev,
      [selectedPartner]: newAccess
    }));
    
    // Update backend
    try {
      await axios.post(`${API}/superadmin/partner-access`, {
        partner_id: selectedPartner,
        game_indices: newAccess
      });
    } catch (err) {
      console.error('Error updating access:', err);
    }
  };

  const selectAllGames = async () => {
    const allIndices = games.map(g => g.game_index);
    
    setPartnerAccess(prev => ({
      ...prev,
      [selectedPartner]: allIndices
    }));
    
    try {
      await axios.post(`${API}/superadmin/partner-access`, {
        partner_id: selectedPartner,
        game_indices: allIndices
      });
    } catch (err) {
      console.error('Error updating access:', err);
    }
  };

  const deselectAllGames = async () => {
    setPartnerAccess(prev => ({
      ...prev,
      [selectedPartner]: []
    }));
    
    try {
      await axios.post(`${API}/superadmin/partner-access`, {
        partner_id: selectedPartner,
        game_indices: []
      });
    } catch (err) {
      console.error('Error updating access:', err);
    }
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPartnerName = (id) => {
    const partner = partners.find(p => p.partner_id === id);
    return partner?.partner_name || id;
  };

  const getPartnerColor = (id) => {
    const partner = partners.find(p => p.partner_id === id);
    return partner?.color || '#666';
  };

  // Show loading while checking session
  if (isLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src={LOGO_URL} 
            alt="TicketASaurus" 
            className="w-40 h-40 mx-auto object-contain"
          />
        </div>

        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold">Super Admin</CardTitle>
            <CardDescription>Partner Access Management</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Email</label>
                <Input
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground">Password</label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              
              {error && (
                <p className="text-sm text-red-500 text-center">{error}</p>
              )}
              
              <Button 
                type="submit" 
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Log In'}
              </Button>
            </form>
            
            <div className="mt-4 pt-4 border-t border-border text-center">
              <a href="/" className="text-sm text-muted-foreground hover:text-foreground">
                ← Back to Main Site
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/10">
      {/* Header - Enhanced Desktop Design */}
      <header className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 border-b border-purple-500/20 shadow-lg">
        <div className="container mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            {/* Left: Logo and Title */}
            <div className="flex items-center gap-3 md:gap-4">
              <div className="hidden sm:block bg-white/10 p-2 rounded-xl backdrop-blur-sm">
                <img src={LOGO_URL} alt="TicketASaurus" className="w-8 h-8 md:w-10 md:h-10 object-contain" />
              </div>
              <div>
                <h1 className="text-lg md:text-2xl font-bold text-white tracking-tight">
                  SuperAdmin Dashboard
                </h1>
                <p className="text-xs md:text-sm text-purple-200/70 hidden sm:block">Partner Access Management</p>
              </div>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2 md:gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white/80 hover:text-white hover:bg-white/10 border border-white/20 text-xs md:text-sm px-2 md:px-4"
                onClick={() => window.location.href = '/'}
              >
                <span className="mr-1 md:mr-2">🏠</span> 
                <span className="hidden sm:inline">Main Site</span>
                <span className="sm:hidden">Home</span>
              </Button>
              <Button 
                variant="destructive" 
                size="sm"
                className="bg-red-600 hover:bg-red-700 shadow-md text-xs md:text-sm px-2 md:px-4"
                onClick={handleLogout}
              >
                <span className="mr-1 md:mr-2">🚪</span> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-2 sm:px-4 py-4 md:py-6">
        {/* Tab Navigation - Enhanced */}
        <div className="bg-card rounded-xl border border-border shadow-sm p-1.5 md:p-2 mb-4 md:mb-6">
          <div className="flex gap-1 flex-wrap">
            <Button
              variant={activeTab === 'users' ? 'default' : 'ghost'}
              size="sm"
              className={`text-xs md:text-sm ${activeTab === 'users' ? 'bg-purple-600 hover:bg-purple-700' : 'hover:bg-muted'}`}
              onClick={() => setActiveTab('users')}
            >
              👥 Users
            </Button>
            <Button
              variant={activeTab === 'events' ? 'default' : 'ghost'}
              size="sm"
              className={`text-xs md:text-sm ${activeTab === 'events' ? 'bg-purple-600 hover:bg-purple-700' : 'hover:bg-muted'}`}
              onClick={() => setActiveTab('events')}
            >
              🎫 Games/Events
            </Button>
            <Button
              variant={activeTab === 'communications' ? 'default' : 'ghost'}
              size="sm"
              className={`text-xs md:text-sm ${activeTab === 'communications' ? 'bg-purple-600 hover:bg-purple-700' : 'hover:bg-muted'}`}
              onClick={() => setActiveTab('communications')}
            >
              📧 Communications
            </Button>
            <Button
              variant={activeTab === 'settings' ? 'default' : 'ghost'}
              size="sm"
              className={`text-xs md:text-sm ${activeTab === 'settings' ? 'bg-purple-600 hover:bg-purple-700' : 'hover:bg-muted'}`}
              onClick={() => setActiveTab('settings')}
            >
              ⚙️ Settings
            </Button>
            <Button
              variant={activeTab === 'logs' ? 'default' : 'ghost'}
              size="sm"
              className={`text-xs md:text-sm ${activeTab === 'logs' ? 'bg-purple-600 hover:bg-purple-700' : 'hover:bg-muted'}`}
              onClick={() => setActiveTab('logs')}
            >
              📋 Access Logs
            </Button>
            <Button
              variant={activeTab === 'activity' ? 'default' : 'ghost'}
              size="sm"
              className={`text-xs md:text-sm ${activeTab === 'activity' ? 'bg-purple-600 hover:bg-purple-700' : 'hover:bg-muted'}`}
              onClick={() => setActiveTab('activity')}
            >
              📝 Activity Log
            </Button>
          </div>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Add New User */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Add New User</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Name</label>
                    <Input
                      placeholder="Partner name"
                      value={newUser.name}
                      onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Password</label>
                    <div className="relative mt-1">
                      <Input
                        type={showNewUserPassword ? "text" : "password"}
                        placeholder="Password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        className="pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewUserPassword(!showNewUserPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showNewUserPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Partner ID (1-2 characters)</label>
                    <Input
                      placeholder="e.g., R, GS, or JD"
                      value={newUser.partner_id}
                      onChange={(e) => {
                        // Allow 1-2 character uppercase
                        const val = e.target.value.toUpperCase().slice(0, 2);
                        setNewUser({...newUser, partner_id: val});
                      }}
                      className="mt-1"
                      maxLength={2}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Used for ticket allocation. Existing IDs: {users.filter(u => u.partner_id).map(u => u.partner_id).join(', ') || 'None'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Role</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3"
                    >
                      <option value="partner">Partner</option>
                      <option value="superadmin">Super Admin</option>
                    </select>
                  </div>
                  {userMessage && (
                    <p className={`text-sm ${userMessage.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>
                      {userMessage}
                    </p>
                  )}
                  <Button type="submit" className="w-full">
                    Create User
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Existing Users */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Existing Users</CardTitle>
                <CardDescription>Manage partner and admin accounts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((user, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg border ${user.is_active ? 'bg-card' : 'bg-muted/50 opacity-60'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div 
                            className={`font-bold ${user.role !== 'superadmin' ? 'cursor-pointer hover:text-blue-500 hover:underline' : ''}`}
                            onClick={() => user.role !== 'superadmin' && handleEmulateUser(user)}
                            title={user.role !== 'superadmin' ? `Click to emulate ${user.name}` : ''}
                          >
                            {user.name}
                            {user.role !== 'superadmin' && (
                              <span className="ml-2 text-xs text-gray-400">👤</span>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Badge variant={user.role === 'superadmin' ? 'destructive' : 'default'}>
                              {user.role}
                            </Badge>
                            {user.partner_id && (
                              <Badge 
                                variant="outline"
                                style={{ 
                                  borderColor: user.partner_id === 'F' ? '#3B82F6' : '#F97316',
                                  color: user.partner_id === 'F' ? '#3B82F6' : '#F97316'
                                }}
                              >
                                Partner {user.partner_id}
                              </Badge>
                            )}
                            <Badge variant={user.is_active ? 'default' : 'secondary'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            {/* Event Manager Toggle - only show for non-superadmin users */}
                            {user.role !== 'superadmin' && (
                              <Badge 
                                variant={user.is_event_manager ? 'default' : 'outline'}
                                className={`cursor-pointer transition-colors ${
                                  user.is_event_manager 
                                    ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                    : 'text-gray-500 hover:text-purple-600 hover:border-purple-600'
                                }`}
                                onClick={() => handleToggleEventManager(user.email, user.is_event_manager)}
                                title={user.is_event_manager ? 'Click to remove Event Manager access' : 'Click to grant Event Manager access'}
                              >
                                {user.is_event_manager ? '✓ Event Manager' : '○ Event Manager'}
                              </Badge>
                            )}
                            {/* Email Toggle - only show for non-superadmin users */}
                            {user.role !== 'superadmin' && (
                              <Badge 
                                variant={(user.receives_emails !== false) ? 'default' : 'outline'}
                                className={`cursor-pointer transition-colors ${
                                  (user.receives_emails !== false)
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                    : 'text-gray-500 hover:text-blue-600 hover:border-blue-600'
                                }`}
                                onClick={() => handleToggleReceivesEmails(user.email, user.receives_emails !== false)}
                                title={(user.receives_emails !== false) ? 'Click to disable emails' : 'Click to enable emails'}
                              >
                                {(user.receives_emails !== false) ? '✓ Email' : '○ Email'}
                              </Badge>
                            )}
                            {/* FE/DV Toggle - only show for non-superadmin users */}
                            {user.role !== 'superadmin' && (() => {
                              const fedvEvent = events.find(e => e.id === 'family-eventsdoctor-visits-2026Season');
                              const hasFEDV = fedvEvent?.assignedUsers?.includes(user.email);
                              return (
                                <Badge 
                                  variant={hasFEDV ? 'default' : 'outline'}
                                  className={`cursor-pointer transition-colors ${
                                    hasFEDV
                                      ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                                      : 'text-gray-500 hover:text-teal-600 hover:border-teal-600'
                                  }`}
                                  onClick={() => handleToggleFEDV(user.email)}
                                  title={hasFEDV ? 'Click to remove FE/DV access' : 'Click to grant FE/DV access'}
                                  data-testid={`fedv-toggle-${user.email}`}
                                >
                                  {hasFEDV ? '✓ FE/DV' : '○ FE/DV'}
                                </Badge>
                              );
                            })()}
                            {/* T/C Toggle - only show for non-superadmin users */}
                            {user.role !== 'superadmin' && (() => {
                              const tcEvent = events.find(e => e.id === 'theaterconcerts-2026');
                              const hasTC = tcEvent?.assignedUsers?.includes(user.email);
                              return (
                                <Badge 
                                  variant={hasTC ? 'default' : 'outline'}
                                  className={`cursor-pointer transition-colors ${
                                    hasTC
                                      ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                      : 'text-gray-500 hover:text-purple-600 hover:border-purple-600'
                                  }`}
                                  onClick={() => handleToggleTC(user.email)}
                                  title={hasTC ? 'Click to remove T/C access' : 'Click to grant T/C access'}
                                  data-testid={`tc-toggle-${user.email}`}
                                >
                                  {hasTC ? '✓ T/C' : '○ T/C'}
                                </Badge>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                          {/* Edit Button - not for superadmin */}
                          {user.role !== 'superadmin' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEditUser(user)}
                            >
                              ✏️ Edit
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              const newPwd = prompt('Enter new password:');
                              if (newPwd) handleUpdateUserPassword(user.email, newPwd);
                            }}
                          >
                            Reset Password
                          </Button>
                          <Button 
                            variant={user.is_active ? 'destructive' : 'default'}
                            size="sm"
                            onClick={() => handleToggleUserActive(user.email, user.is_active)}
                          >
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          {user.role !== 'superadmin' && (
                            <Button 
                              variant="outline"
                              size="sm"
                              className="text-red-500 border-red-500 hover:bg-red-500/10"
                              onClick={() => handleDeleteUser(user.email, user.name)}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : activeTab === 'events' ? (
          /* Events Tab - Create Events & Assign Users */
          <div className="space-y-6">
            {/* Create New Event */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">➕ Create New Event</CardTitle>
                <CardDescription>Add a new team/season event</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium">Team Name</label>
                    <Input
                      placeholder="e.g., Dallas Cowboys"
                      value={newEventForm.name}
                      onChange={(e) => setNewEventForm({...newEventForm, name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Event Type</label>
                    <select
                      value={newEventForm.sport}
                      onChange={(e) => setNewEventForm({...newEventForm, sport: e.target.value})}
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3"
                    >
                      <option value="basketball">🏀 Basketball</option>
                      <option value="hockey">🏒 Hockey</option>
                      <option value="football">🏈 Football</option>
                      <option value="baseball">⚾ Baseball</option>
                      <option value="concerts">🎭 Concerts/Theater</option>
                      <option value="family">🏠 Family Events/Doctor</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Season</label>
                    <Input
                      placeholder="e.g., 2025-2026"
                      value={newEventForm.season}
                      onChange={(e) => setNewEventForm({...newEventForm, season: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Seats</label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      placeholder="6"
                      value={newEventForm.seat_count}
                      onChange={(e) => setNewEventForm({...newEventForm, seat_count: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Parking</label>
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      placeholder="2"
                      value={newEventForm.parking_count}
                      onChange={(e) => setNewEventForm({...newEventForm, parking_count: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button className="w-full" onClick={handleCreateSeasonEvent}>
                      Create Event
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Existing Events with User Assignment */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">📅 Events & User Assignment</CardTitle>
                <CardDescription>Manage events and assign users to each event</CardDescription>
                {events.some(e => e.adminArchived) && (
                  <div className="flex justify-center pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="toggle-show-archived-events"
                      onClick={() => setShowArchivedEvents(v => !v)}
                    >
                      📦 {showArchivedEvents ? 'Hide' : 'Show'} Archived ({events.filter(e => e.adminArchived).length})
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {events.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No events created yet. Create your first event above.</p>
                ) : (
                  [...events.filter(e => !e.adminArchived), ...(showArchivedEvents ? events.filter(e => e.adminArchived) : [])].map(event => (
                    <div 
                      key={event.id} 
                      data-testid={`admin-event-card-${event.id}`}
                      className={`border border-border rounded-lg p-4 ${event.status === 'coming_soon' ? 'opacity-70' : ''} ${event.adminArchived ? 'border-dashed bg-muted/20' : ''}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                            event.sport === 'basketball' ? 'bg-blue-600' :
                            event.sport === 'hockey' ? 'bg-green-700' :
                            event.sport === 'football' ? 'bg-orange-600' :
                            'bg-red-600'
                          }`}>
                            {getSportEmoji(event.sport)}
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{event.name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {event.sport.charAt(0).toUpperCase() + event.sport.slice(1)} • {event.season} Season • {event.id === 'mavericks-2025' ? games.length : event.gamesCount} games
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {event.adminArchived && <Badge variant="secondary" data-testid={`archived-badge-${event.id}`}>📦 Archived</Badge>}
                          <Badge variant={event.status === 'active' ? 'default' : 'secondary'}>
                            {event.status === 'active' ? 'Active' : 'Inactive'}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`archive-event-btn-${event.id}`}
                            onClick={() => handleToggleEventArchived(event.id)}
                          >
                            📦 {event.adminArchived ? 'Unarchive' : 'Archive'}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditEvent(event)}
                          >
                            ✏️ Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/event-admin?event=${event.id}`)}
                          >
                            ⚙️ Manage
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => handleDeleteSeasonEvent(event.id)}
                          >
                            🗑️ Delete
                          </Button>
                        </div>
                      </div>
                      
                      {event.adminArchived && <SeasonRecapCard eventId={event.id} users={users} />}
                      {/* Assigned Users */}
                      <div className="bg-muted/30 rounded-lg p-3">
                        <h4 className="text-sm font-semibold mb-3">👥 Assigned Users</h4>
                        <div className="flex flex-wrap gap-2">
                          {/* Show users assigned to this event */}
                          {users.filter(u => u.role !== 'superadmin').map(user => {
                            const isAssigned = event.assignedUsers.includes(user.email);
                            
                            return isAssigned ? (
                              <div 
                                key={user.email}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/50"
                              >
                                <span className="text-sm font-medium">{user.name}</span>
                                <span className="text-xs text-muted-foreground">({user.partner_id})</span>
                                <button 
                                  className="text-red-400 hover:text-red-500 ml-1"
                                  title="Remove from event"
                                  onClick={() => handleRemoveUserFromEvent(event.id, user.email)}
                                >
                                  ✕
                                </button>
                              </div>
                            ) : null;
                          })}
                          
                          {/* Add User dropdown */}
                          <div className="relative" ref={openUserDropdown === event.id ? dropdownRef : null}>
                            <button 
                              className="px-3 py-1.5 rounded-full border border-dashed border-border hover:border-primary hover:bg-primary/10 text-sm text-muted-foreground"
                              onClick={(e) => {
                                if (openUserDropdown === event.id) {
                                  setOpenUserDropdown(null);
                                } else {
                                  // Decide direction based on viewport space below the button
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  // Roughly 60px per row × at most 6 visible rows + 16px padding ≈ 376px
                                  const estimatedMenuHeight = Math.min(376, (users.filter(u => u.role !== 'superadmin' && !event.assignedUsers.includes(u.email)).length || 1) * 44 + 16);
                                  setDropdownDirection(spaceBelow < estimatedMenuHeight + 24 ? 'up' : 'down');
                                  setOpenUserDropdown(event.id);
                                }
                              }}
                            >
                              + Add User
                            </button>
                            {openUserDropdown === event.id && (
                              <div className={`absolute left-0 bg-popover border border-border rounded-lg shadow-lg z-50 min-w-[200px] max-h-[300px] overflow-y-auto ${dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                                {users.filter(u => u.role !== 'superadmin' && !event.assignedUsers.includes(u.email)).map(user => (
                                  <button
                                    key={user.email}
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted/50 first:rounded-t-lg last:rounded-b-lg"
                                    onClick={() => {
                                      handleAssignUserToEvent(event.id, user.email);
                                      setOpenUserDropdown(null);
                                    }}
                                  >
                                    {user.name} ({user.partner_id})
                                  </button>
                                ))}
                                {users.filter(u => u.role !== 'superadmin' && !event.assignedUsers.includes(u.email)).length === 0 && (
                                  <p className="px-3 py-2 text-sm text-muted-foreground">All users assigned</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        ) : activeTab === 'settings' ? (
          /* Settings Tab - Comprehensive Admin Settings */
          <div className="space-y-6">
            {/* Status Messages */}
            {settingsMessage && (
              <div className={`p-4 rounded-lg ${settingsMessage.includes('✅') ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {settingsMessage}
              </div>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Backup & Restore */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">💾 Backup & Restore</CardTitle>
                  <CardDescription>Export and import your database</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <BackupExportRestore onBackup={handleBackup} onRestore={handleRestore} backupMessage={backupMessage} />
                  
                  {/* Scheduled Backup Email */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium mb-2">📧 Scheduled Backup Email</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Receive a weekly backup file via email.
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="backup_email_enabled"
                          checked={settings.backup_email_enabled || false}
                          onChange={(e) => setSettings({...settings, backup_email_enabled: e.target.checked})}
                          className="h-4 w-4"
                        />
                        <label htmlFor="backup_email_enabled" className="text-sm font-medium">
                          Enable Weekly Backup Email
                        </label>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Email Address</label>
                        <Input
                          type="email"
                          value={settings.backup_email_address || ''}
                          onChange={(e) => setSettings({...settings, backup_email_address: e.target.value})}
                          placeholder="backup@example.com"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-sm font-medium mb-1">Day</label>
                          <select
                            value={settings.backup_email_day || 'sunday'}
                            onChange={(e) => setSettings({...settings, backup_email_day: e.target.value})}
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                          >
                            <option value="monday">Monday</option>
                            <option value="tuesday">Tuesday</option>
                            <option value="wednesday">Wednesday</option>
                            <option value="thursday">Thursday</option>
                            <option value="friday">Friday</option>
                            <option value="saturday">Saturday</option>
                            <option value="sunday">Sunday</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Time</label>
                          <Input
                            type="time"
                            value={settings.backup_email_time || '06:00'}
                            onChange={(e) => setSettings({...settings, backup_email_time: e.target.value})}
                          />
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          onClick={async () => {
                            try {
                              setBackupEmailMessage('⏳ Saving...');
                              await axios.post(`${API}/superadmin/global-settings`, {
                                type: 'backup_email',
                                backup_email_enabled: settings.backup_email_enabled,
                                backup_email_address: settings.backup_email_address,
                                backup_email_day: settings.backup_email_day,
                                backup_email_time: settings.backup_email_time
                              });
                              setBackupEmailMessage('✅ Backup email settings saved!');
                              setTimeout(() => setBackupEmailMessage(''), 3000);
                            } catch (err) {
                              setBackupEmailMessage('❌ Failed to save settings');
                            }
                          }}
                          className="flex-1"
                          disabled={!settings.backup_email_address}
                        >
                          💾 Save Settings
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={async () => {
                            if (sendingBackupEmail) return; // Prevent double-click
                            try {
                              setSendingBackupEmail(true);
                              setBackupEmailMessage('⏳ Sending test backup...');
                              const response = await axios.post(`${API}/superadmin/send-backup-email`, {
                                email: settings.backup_email_address
                              });
                              if (response.data.success) {
                                setBackupEmailMessage('✅ Test backup sent!');
                              } else {
                                setBackupEmailMessage(`❌ ${response.data.error || 'Failed to send'}`);
                              }
                              setTimeout(() => setBackupEmailMessage(''), 5000);
                            } catch (err) {
                              setBackupEmailMessage('❌ Failed to send test backup');
                            } finally {
                              setSendingBackupEmail(false);
                            }
                          }}
                          disabled={!settings.backup_email_address || sendingBackupEmail}
                        >
                          {sendingBackupEmail ? '⏳ Sending...' : '📤 Send Now'}
                        </Button>
                      </div>
                      
                      {backupEmailMessage && (
                        <p className={`text-sm text-center ${backupEmailMessage.includes('✅') ? 'text-green-600' : backupEmailMessage.includes('❌') ? 'text-red-500' : 'text-blue-500'}`}>
                          {backupEmailMessage}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Perpetual Events Renewal */}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-medium mb-2" data-testid="perpetual-events-header">🔄 Perpetual Events Renewal</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Automatically creates next-year copies of annual/perpetual events (e.g. birthdays, anniversaries). Runs automatically on the 1st of each month.
                    </p>
                    <div className="space-y-2">
                      <Button 
                        data-testid="process-perpetual-events-btn"
                        variant="outline"
                        onClick={async () => {
                          try {
                            setBackupEmailMessage('⏳ Processing perpetual events...');
                            const response = await axios.post(`${API}/superadmin/process-perpetual-events`);
                            if (response.data.success) {
                              setBackupEmailMessage(`✅ ${response.data.message}`);
                            } else {
                              setBackupEmailMessage(`❌ ${response.data.error || 'Failed'}`);
                            }
                            setTimeout(() => setBackupEmailMessage(''), 5000);
                          } catch (err) {
                            setBackupEmailMessage('❌ Failed to process perpetual events');
                            setTimeout(() => setBackupEmailMessage(''), 3000);
                          }
                        }}
                      >
                        🔄 Run Now
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">📝 Footer Settings</CardTitle>
                  <CardDescription>Customize the footer text displayed on all pages</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Footer Text</label>
                    <Input
                      value={settings.footer_text || ''}
                      onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                      placeholder="Enter footer text (e.g., © 2025 Your Company)"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This text appears at the bottom of the ticket dashboard
                    </p>
                  </div>

                  <Button onClick={handleSaveSettings} className="w-full">
                    💾 Save Footer Settings
                  </Button>
                  
                  {settingsMessage && (
                    <p className={`text-sm text-center ${settingsMessage.includes('✅') ? 'text-green-600' : 'text-red-500'}`}>
                      {settingsMessage}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* SMTP Email Settings - Full Width */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">📧 SMTP Email Settings</CardTitle>
                <CardDescription>Configure email server for sending notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">SMTP Host</label>
                    <Input
                      value={settings.smtp_host || ''}
                      onChange={(e) => setSettings({...settings, smtp_host: e.target.value})}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">SMTP Port</label>
                    <Input
                      type="number"
                      value={settings.smtp_port || 587}
                      onChange={(e) => setSettings({...settings, smtp_port: parseInt(e.target.value) || 587})}
                      placeholder="587"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Username</label>
                    <Input
                      value={settings.smtp_username || ''}
                      onChange={(e) => setSettings({...settings, smtp_username: e.target.value})}
                      placeholder="your-email@gmail.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Password / App Password</label>
                    <Input
                      type="password"
                      value={settings.smtp_password || ''}
                      onChange={(e) => setSettings({...settings, smtp_password: e.target.value})}
                      placeholder="••••••••••••••••"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">From Email</label>
                    <Input
                      value={settings.smtp_from_email || ''}
                      onChange={(e) => setSettings({...settings, smtp_from_email: e.target.value})}
                      placeholder="noreply@yourcompany.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">From Name</label>
                    <Input
                      value={settings.smtp_from_name || ''}
                      onChange={(e) => setSettings({...settings, smtp_from_name: e.target.value})}
                      placeholder="TicketASaurus"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="smtp_use_tls"
                    checked={settings.smtp_use_tls !== false}
                    onChange={(e) => setSettings({...settings, smtp_use_tls: e.target.checked})}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="smtp_use_tls" className="text-sm">Use TLS/SSL encryption (recommended)</label>
                </div>
                
                <div className="flex gap-3">
                  <Button onClick={handleSaveSmtp} className="flex-1">
                    💾 Save SMTP Settings
                  </Button>
                  <Button onClick={handleTestSmtp} variant="outline" className="flex-1">
                    🧪 Test Connection
                  </Button>
                </div>
                
                {smtpMessage && (
                  <p className={`text-sm text-center ${smtpMessage.includes('✅') ? 'text-green-600' : smtpMessage.includes('❌') ? 'text-red-500' : 'text-blue-500'}`}>
                    {smtpMessage}
                  </p>
                )}
                
                <div className="p-3 bg-muted/50 rounded-lg text-sm">
                  <p className="font-medium mb-1">📌 Gmail Setup Tips:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Use smtp.gmail.com with port 587</li>
                    <li>Enable 2FA and create an App Password</li>
                    <li>Use the App Password (not your regular password)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : activeTab === 'logs' ? (
          /* Access Logs Tab */
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>🔐 Access Logs</CardTitle>
                <CardDescription>Recent login attempts and security events • Showing last 50 entries</CardDescription>
              </div>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={async () => {
                  if (window.confirm('Are you sure you want to purge ALL access logs? This cannot be undone.')) {
                    try {
                      await axios.delete(`${API}/superadmin/access-logs`);
                      setAccessLogs([]);
                      alert('✅ Access logs purged successfully!');
                    } catch (err) {
                      alert('❌ Failed to purge logs');
                    }
                  }
                }}
              >
                🗑️ Purge All Logs
              </Button>
            </CardHeader>
            <CardContent>
              {accessLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No access logs yet. Login activity will appear here.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-3 font-semibold">Timestamp</th>
                        <th className="text-left p-3 font-semibold">User</th>
                        <th className="text-left p-3 font-semibold">IP Address</th>
                        <th className="text-left p-3 font-semibold">Action</th>
                        <th className="text-left p-3 font-semibold">Status</th>
                        <th className="text-left p-3 font-semibold">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accessLogs.map((log, idx) => (
                        <tr key={idx} className={`border-b hover:bg-muted/30 ${!log.success ? 'bg-red-900/40' : ''}`}>
                          <td className={`p-2 font-mono text-xs whitespace-nowrap ${!log.success ? 'text-red-200' : ''}`}>
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${!log.success ? 'text-red-200' : ''}`}>{log.user_name || 'Unknown'}</span>
                              <span className={`text-xs ${!log.success ? 'text-red-300' : 'text-muted-foreground'}`}>({log.email})</span>
                              {log.partner_id && (
                                <Badge variant="outline" className="text-xs">Partner {log.partner_id}</Badge>
                              )}
                              {log.role === 'superadmin' && (
                                <Badge variant="secondary" className="text-xs">Admin</Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-2">
                            <span className={`font-mono text-xs px-2 py-1 rounded ${!log.success ? 'bg-red-800 text-red-100' : 'bg-muted'}`}>
                              {log.ip_address || 'N/A'}
                            </span>
                          </td>
                          <td className={`p-2 capitalize ${!log.success ? 'text-red-200' : ''}`}>{log.action}</td>
                          <td className="p-2">
                            {log.success ? (
                              <Badge variant="default" className="bg-green-600">✓ Success</Badge>
                            ) : (
                              <Badge variant="destructive" className="bg-red-600 text-white font-semibold">✗ Failed</Badge>
                            )}
                          </td>
                          <td className="p-2 text-xs">
                            {log.failure_reason && (
                              <span className="text-red-300 font-semibold">{log.failure_reason}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : activeTab === 'activity' ? (
          /* Activity Logs Tab */
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>📝 Activity Log</CardTitle>
                <CardDescription>Audit trail for ticket assignments, user management, and system changes</CardDescription>
              </div>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={async () => {
                  if (window.confirm('Are you sure you want to purge ALL activity logs? This cannot be undone.')) {
                    try {
                      await axios.delete(`${API}/superadmin/activity-logs`);
                      setActivityLogs([]);
                      alert('✅ Activity logs purged successfully!');
                    } catch (err) {
                      alert('❌ Failed to purge logs');
                    }
                  }
                }}
              >
                🗑️ Purge All
              </Button>
            </CardHeader>
            <CardContent>
              {activityLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No activity logs yet. Actions like ticket assignments and user management will appear here.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-2 font-semibold">Timestamp</th>
                        <th className="text-left p-2 font-semibold">User</th>
                        <th className="text-left p-2 font-semibold">Action</th>
                        <th className="text-left p-2 font-semibold">Details</th>
                        <th className="text-left p-2 font-semibold">IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLogs.map((log, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/30">
                          <td className="p-2 font-mono text-xs whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="p-2 text-sm">
                            {log.user_name || log.user_email ? (
                              <div className="flex flex-col">
                                {log.user_name && <span className="font-medium">{log.user_name}</span>}
                                {log.user_email && <span className="text-xs text-muted-foreground">{log.user_email}</span>}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">System</span>
                            )}
                          </td>
                          <td className="p-2">
                            {log.action === 'ticket_assigned' && (
                              <Badge className="bg-pink-600 text-white border-0 whitespace-nowrap">🎟️ Ticket</Badge>
                            )}
                            {log.action === 'user_created' && (
                              <Badge className="bg-green-600 text-white border-0 whitespace-nowrap">👤 User Created</Badge>
                            )}
                            {log.action === 'user_deleted' && (
                              <Badge className="bg-red-600 text-white border-0 whitespace-nowrap">🗑️ User Deleted</Badge>
                            )}
                            {log.action === 'settings_updated' && (
                              <Badge className="bg-blue-600 text-white border-0 whitespace-nowrap">⚙️ Settings</Badge>
                            )}
                            {!['ticket_assigned', 'user_created', 'user_deleted', 'settings_updated'].includes(log.action) && (
                              <Badge className="bg-gray-600 text-white border-0">{log.action}</Badge>
                            )}
                          </td>
                          <td className="p-2 text-sm text-left">
                            {log.details}
                          </td>
                          <td className="p-2">
                            <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                              {log.ip_address || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : activeTab === 'communications' ? (
          /* Communications Tab */
          <div className="space-y-6">
            {/* Email Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">{emailStats.total_campaigns}</div>
                  <div className="text-sm text-muted-foreground">Campaigns Sent</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">{emailStats.total_emails_sent}</div>
                  <div className="text-sm text-muted-foreground">Total Emails Delivered</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>📧 Email Communications</CardTitle>
                <CardDescription>Send emails to users and manage email templates</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status Message */}
                {emailMessage && (
                  <div className={`p-3 rounded-lg ${emailMessage.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {emailMessage}
                  </div>
                )}

                {/* Quick Send Section */}
                <div className="border rounded-lg p-4 space-y-4">
                  <h3 className="font-semibold text-lg">📤 Quick Send Email</h3>
                  
                  <div>
                    <label className="text-sm font-medium">Recipients</label>
                    <select 
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3"
                      value={emailForm.recipients}
                      onChange={(e) => setEmailForm({...emailForm, recipients: e.target.value})}
                    >
                      <option value="all">All Users ({users.filter(u => u.role !== 'superadmin' && u.receives_emails !== false).length} recipients)</option>
                      <option value="partners">All Partners ({users.filter(u => u.role === 'partner' && u.receives_emails !== false).length} recipients)</option>
                      {users.filter(u => u.role !== 'superadmin').map((user, idx) => (
                        <option key={idx} value={user.email}>
                          {user.name} ({user.email}) {user.receives_emails === false ? '⛔' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Subject</label>
                    <Input 
                      placeholder="Email subject line..."
                      className="mt-1"
                      value={emailForm.subject}
                      onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Message</label>
                    <textarea 
                      className="mt-1 w-full h-32 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                      placeholder="Type your message here..."
                      value={emailForm.message}
                      onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                    />
                  </div>
                  
                  <Button 
                    className="w-full" 
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !emailForm.subject || !emailForm.message}
                  >
                    {sendingEmail ? '⏳ Sending...' : '📧 Send Email'}
                  </Button>
                </div>

                {/* Email History */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-lg">📜 Email History</h3>
                    {emailHistory.length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-500 border-red-300 hover:bg-red-50"
                        onClick={handleClearEmailHistory}
                      >
                        🗑️ Clear History
                      </Button>
                    )}
                  </div>
                  
                  {emailHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No emails sent yet</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {emailHistory.map((email, idx) => (
                        <div key={idx} className="p-3 bg-muted/30 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div className="font-medium text-sm">{email.subject}</div>
                            <Badge variant={email.failed_count > 0 ? 'destructive' : 'default'}>
                              {email.sent_count} sent {email.failed_count > 0 ? `/ ${email.failed_count} failed` : ''}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {new Date(email.timestamp).toLocaleString()}
                            {email.template_type === 'request_access' ? (
                              <span> • From: <span className="text-cyan-500 font-medium">{email.applicant_name} ({email.applicant_email})</span></span>
                            ) : (
                              <>
                                {' • Partners: '}{email.recipients?.length || 0}
                                {(email.assignee_sent_count > 0 || email.assignees_notified?.length > 0) && (
                                  <span className="text-green-600 font-medium"> • Assignees: {email.assignee_sent_count || email.assignees_notified?.length || 0}</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Email Templates Section */}
                <div className="border rounded-lg p-4 space-y-4">
                  <h3 className="font-semibold text-lg">📋 Email Templates</h3>
                  <p className="text-sm text-muted-foreground">Pre-configured email templates for common communications</p>
                  
                  {/* Preview All Templates Button */}
                  <div className="flex justify-end">
                    <Button
                      data-testid="preview-all-emails-btn"
                      variant="outline"
                      size="sm"
                      className="border-orange-400 text-orange-700 hover:bg-orange-50"
                      onClick={() => handleOpenEmailPreview('all')}
                    >
                      Preview All Templates
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Game Reminder 24h with automation */}
                    <div className="border-2 rounded-lg p-4 border-blue-400 bg-blue-50/50 min-h-[180px] flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-blue-700 text-base">🎟️ Game Reminder (24h)</div>
                        <div className="flex gap-2">
                          <Button 
                            data-testid="preview-daily-email-btn"
                            variant="outline" 
                            size="sm"
                            className="border-blue-400 text-blue-700 hover:bg-blue-100"
                            onClick={() => handleOpenEmailPreview('daily')}
                          >
                            Preview
                          </Button>
                          <Button 
                            variant="default" 
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={handleSendGameReminder}
                            disabled={sendingEmail}
                          >
                            Send Now
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground mb-4">Send reminder for games in next 24 hours</div>
                      
                      {/* Automation controls */}
                      <div className="border-t pt-3 mt-auto">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              className="sr-only peer"
                              checked={automationSettings.reminder_24h_enabled}
                              onChange={(e) => {
                                const newSettings = { ...automationSettings, reminder_24h_enabled: e.target.checked };
                                handleSaveAutomation(newSettings);
                              }}
                            />
                            <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                          <span className="text-sm">Auto-send daily at:</span>
                          <Input 
                            type="time" 
                            value={automationSettings.reminder_24h_time}
                            onChange={(e) => {
                              const newSettings = { ...automationSettings, reminder_24h_time: e.target.value };
                              handleSaveAutomation(newSettings);
                            }}
                            className="w-28 h-8"
                            disabled={!automationSettings.reminder_24h_enabled}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {automationSettings.reminder_24h_enabled 
                            ? `✅ Auto-sends daily at ${automationSettings.reminder_24h_time}` 
                            : '⏸️ Automation disabled'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Game Reminder 1 Week with automation */}
                    <div className="border-2 rounded-lg p-4 border-green-400 bg-green-50/50 min-h-[180px] flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-green-700 text-base">📅 Game Reminder (1 week)</div>
                        <div className="flex gap-2">
                          <Button 
                            data-testid="preview-weekly-email-btn"
                            variant="outline" 
                            size="sm"
                            className="border-green-400 text-green-700 hover:bg-green-100"
                            onClick={() => handleOpenEmailPreview('weekly')}
                          >
                            Preview
                          </Button>
                          <Button 
                            variant="default" 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={handleSendWeeklyGameReminder}
                            disabled={sendingEmail}
                          >
                            Send Now
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground mb-4">Send list of all games in next 7 days</div>
                      
                      {/* Automation controls */}
                      <div className="border-t pt-3 mt-auto">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              className="sr-only peer"
                              checked={automationSettings.reminder_week_enabled}
                              onChange={(e) => {
                                const newSettings = { ...automationSettings, reminder_week_enabled: e.target.checked };
                                handleSaveAutomation(newSettings);
                              }}
                            />
                            <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                          </label>
                          <span className="text-sm">Auto-send on:</span>
                          <select 
                            value={automationSettings.reminder_week_day}
                            onChange={(e) => {
                              const newSettings = { ...automationSettings, reminder_week_day: e.target.value };
                              handleSaveAutomation(newSettings);
                            }}
                            className="h-8 text-sm border rounded px-2"
                            disabled={!automationSettings.reminder_week_enabled}
                          >
                            <option value="monday">Mon</option>
                            <option value="tuesday">Tue</option>
                            <option value="wednesday">Wed</option>
                            <option value="thursday">Thu</option>
                            <option value="friday">Fri</option>
                            <option value="saturday">Sat</option>
                            <option value="sunday">Sun</option>
                          </select>
                          <Input 
                            type="time" 
                            value={automationSettings.reminder_week_time}
                            onChange={(e) => {
                              const newSettings = { ...automationSettings, reminder_week_time: e.target.value };
                              handleSaveAutomation(newSettings);
                            }}
                            className="w-28 h-8"
                            disabled={!automationSettings.reminder_week_enabled}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {automationSettings.reminder_week_enabled 
                            ? `✅ Auto-sends every ${automationSettings.reminder_week_day.charAt(0).toUpperCase() + automationSettings.reminder_week_day.slice(1)} at ${automationSettings.reminder_week_time}` 
                            : '⏸️ Automation disabled'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Friends & Family Assignee Emails - styled as card */}
                    <div className="border-2 rounded-lg p-4 border-amber-400 bg-amber-50/50 min-h-[180px] flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-amber-700 text-base">👨‍👩‍👧‍👦 Friends & Family</div>
                        <Button 
                          data-testid="preview-assignee-email-btn"
                          variant="outline" 
                          size="sm"
                          className="border-amber-400 text-amber-700 hover:bg-amber-100"
                          onClick={() => handleOpenEmailPreview('assignee')}
                        >
                          Preview
                        </Button>
                      </div>
                      <div className="text-sm text-muted-foreground mb-4">Preview emails sent to friends & family when tickets are assigned to them</div>
                      <div className="border-t pt-3 mt-auto">
                        <div className="text-xs text-muted-foreground">
                          📧 Sent automatically when daily/weekly reminders go out
                        </div>
                      </div>
                    </div>
                    
                    {/* Season Summary - styled as card */}
                    <div className="border-2 rounded-lg p-4 border-gray-300 bg-gray-50/50 min-h-[180px] flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-gray-700 text-base">📊 Season Summary</div>
                        <Button 
                          variant="default" 
                          size="sm"
                          className="bg-gray-600 hover:bg-gray-700 text-white"
                          onClick={() => {
                            setEmailForm({
                              ...emailForm,
                              subject: '📊 Season Ticket Summary',
                              message: 'Hi there!\n\nHere is a summary of your ticket allocations for this season. Please log into the dashboard to view your complete allocation history.\n\nThank you for being part of our ticket group!'
                            });
                          }}
                        >
                          Use Template
                        </Button>
                      </div>
                      <div className="text-sm text-muted-foreground mb-4">Send ticket allocation summary to partners</div>
                      <div className="border-t pt-3 mt-auto">
                        <div className="text-xs text-muted-foreground">
                          📝 Fills in the email form above with template content
                        </div>
                      </div>
                    </div>
                    
                    {/* Announcement - styled as card */}
                    <div className="border-2 rounded-lg p-4 border-purple-300 bg-purple-50/50 min-h-[180px] flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-purple-700 text-base">📢 Announcement</div>
                        <Button 
                          variant="default" 
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={() => {
                            setEmailForm({
                              ...emailForm,
                              subject: '📢 Important Announcement',
                              message: 'Hi everyone!\n\nWe have an important update to share with you.\n\n[Your announcement here]\n\nThank you!'
                            });
                          }}
                        >
                          Use Template
                        </Button>
                      </div>
                      <div className="text-sm text-muted-foreground mb-4">General announcement to all users</div>
                      <div className="border-t pt-3 mt-auto">
                        <div className="text-xs text-muted-foreground">
                          📝 Fills in the email form above with template content
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SMTP Settings Link */}
                <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
                  <div>
                    <h3 className="font-semibold">📬 Request Access Notifications</h3>
                    <p className="text-sm text-muted-foreground mb-3">Where should "Request Access" submissions be sent?</p>
                    <div className="flex gap-2 items-center">
                      <Input
                        data-testid="request-access-email-input"
                        type="email"
                        placeholder="e.g. owner@example.com"
                        value={settings.request_access_email || ''}
                        onChange={(e) => setSettings({...settings, request_access_email: e.target.value})}
                        className="flex-1"
                      />
                      <Button
                        data-testid="save-request-access-email-btn"
                        variant="outline"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/superadmin/smtp-settings`, {
                              smtp_host: settings.smtp_host || '',
                              smtp_port: settings.smtp_port || 587,
                              smtp_username: settings.smtp_username || '',
                              smtp_password: settings.smtp_password || '',
                              smtp_from_email: settings.smtp_from_email || '',
                              smtp_from_name: settings.smtp_from_name || '',
                              smtp_use_tls: settings.smtp_use_tls !== false,
                              request_access_email: settings.request_access_email || ''
                            });
                            setEmailMessage('✅ Request Access email saved!');
                            setTimeout(() => setEmailMessage(''), 3000);
                          } catch (err) {
                            setEmailMessage('❌ Error saving Request Access email');
                            setTimeout(() => setEmailMessage(''), 3000);
                          }
                        }}
                      >
                        Save
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {settings.request_access_email 
                        ? `Requests will be sent to: ${settings.request_access_email}` 
                        : `Not set — requests will default to the SMTP "From" address (${settings.smtp_from_email || 'not configured'})`}
                    </p>
                  </div>
                  <div className="border-t pt-3 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">⚙️ SMTP Configuration</h3>
                      <p className="text-sm text-muted-foreground">Configure email server settings</p>
                    </div>
                    <Button variant="outline" onClick={() => setActiveTab('settings')}>
                      Go to Settings
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}

        {/* Edit Event Modal */}

        {/* Email Preview Modal */}
        {showEmailPreview && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="email-preview-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="p-4 border-b flex items-center justify-between shrink-0">
                <div>
                  <h2 className="text-lg font-bold">Email Preview</h2>
                  <p className="text-sm text-gray-500">
                    {previewTemplateType === 'all' ? 'All Templates' : 
                     previewTemplateType === 'daily' ? 'Daily Reminder (24h)' :
                     previewTemplateType === 'weekly' ? 'Weekly Reminder (7 day)' :
                     'Assignee Notification'}
                    {filteredPreviews.length > 0 && ` — ${previewIndex + 1} of ${filteredPreviews.length}`}
                  </p>
                </div>
                <button 
                  data-testid="close-email-preview-btn"
                  onClick={() => setShowEmailPreview(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2"
                >
                  &times;
                </button>
              </div>

              {/* Controls */}
              <div className="px-4 py-3 border-b flex items-center gap-3 flex-wrap shrink-0 bg-gray-50" style={{ position: 'relative', zIndex: 10 }}>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">User:</label>
                  <select 
                    data-testid="email-preview-user-select"
                    className="h-9 text-sm border border-gray-300 rounded-md px-3 min-w-[260px] cursor-pointer appearance-auto"
                    style={{ backgroundColor: '#fff', color: '#111', position: 'relative', zIndex: 20 }}
                    value={previewFilterUser}
                    onChange={(e) => { setPreviewFilterUser(e.target.value); setPreviewIndex(0); }}
                  >
                    <option value="all">All Users ({previewPreviews.length})</option>
                    {[...new Set(previewPreviews.map(p => p.user_email))].map(email => {
                      const preview = previewPreviews.find(p => p.user_email === email);
                      return (
                        <option key={email} value={email}>
                          {preview?.user_name || email} ({email})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {previewTemplateType === 'all' && filteredPreviews.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className={`text-xs cursor-default ${filteredPreviews[previewIndex]?.template_type === 'daily' ? 'bg-blue-100 text-blue-700 border-blue-300' : ''}`}>
                      {filteredPreviews[previewIndex]?.template_type === 'daily' ? 'Daily' : 
                       filteredPreviews[previewIndex]?.template_type === 'weekly' ? 'Weekly' : 'Assignee'}
                    </Badge>
                  </div>
                )}

                {/* Navigation */}
                {filteredPreviews.length > 1 && (
                  <div className="flex items-center gap-2 ml-auto">
                    <Button
                      data-testid="email-preview-prev-btn"
                      variant="outline"
                      size="sm"
                      disabled={previewIndex === 0}
                      onClick={() => setPreviewIndex(i => Math.max(0, i - 1))}
                    >
                      Prev
                    </Button>
                    <span className="text-sm text-gray-500 min-w-[60px] text-center">
                      {previewIndex + 1} / {filteredPreviews.length}
                    </span>
                    <Button
                      data-testid="email-preview-next-btn"
                      variant="outline"
                      size="sm"
                      disabled={previewIndex >= filteredPreviews.length - 1}
                      onClick={() => setPreviewIndex(i => Math.min(filteredPreviews.length - 1, i + 1))}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>

              {/* Preview Content */}
              <div className="flex-1 overflow-auto p-4">
                {previewLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading previews...</div>
                  </div>
                ) : filteredPreviews.length === 0 ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center text-gray-500">
                      <p className="text-lg font-medium">No previews available</p>
                      <p className="text-sm mt-1">No upcoming games found to generate email previews</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Info bar */}
                    <div className="mb-3 flex items-center gap-3 text-sm">
                      <span className="font-medium text-gray-700">To: {filteredPreviews[previewIndex]?.user_name || 'Unknown'}</span>
                      <span className="text-gray-400">({filteredPreviews[previewIndex]?.user_email})</span>
                      <Badge variant="outline" className="text-xs">
                        {filteredPreviews[previewIndex]?.template_type}
                      </Badge>
                    </div>
                    <div className="mb-2 text-sm text-gray-600">
                      <strong>Subject:</strong> {filteredPreviews[previewIndex]?.subject}
                    </div>
                    {/* Email HTML rendered in iframe */}
                    <iframe
                      data-testid="email-preview-iframe"
                      title="Email Preview"
                      srcDoc={filteredPreviews[previewIndex]?.html || ''}
                      className="w-full border rounded-lg bg-white"
                      style={{ height: '500px' }}
                      sandbox="allow-same-origin"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {editingEvent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4">✏️ Edit Event</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Event Name</label>
                    <Input
                      value={editEventForm.name}
                      onChange={(e) => setEditEventForm({...editEventForm, name: e.target.value})}
                      placeholder="Dallas Mavericks"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Event Type</label>
                    <select
                      className="w-full px-3 py-2 rounded-md border border-input bg-background"
                      value={editEventForm.sport}
                      onChange={(e) => setEditEventForm({...editEventForm, sport: e.target.value})}
                    >
                      <option value="basketball">🏀 Basketball</option>
                      <option value="hockey">🏒 Hockey</option>
                      <option value="football">🏈 Football</option>
                      <option value="baseball">⚾ Baseball</option>
                      <option value="concerts">🎭 Concerts/Theater</option>
                      <option value="family">🏠 Family Events/Doctor</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Season</label>
                    <Input
                      value={editEventForm.season}
                      onChange={(e) => setEditEventForm({...editEventForm, season: e.target.value})}
                      placeholder="2025-2026"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select
                      className="w-full px-3 py-2 rounded-md border border-input bg-background"
                      value={editEventForm.status}
                      onChange={(e) => setEditEventForm({...editEventForm, status: e.target.value})}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="coming_soon">Coming Soon</option>
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Primary Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editEventForm.primaryColor}
                          onChange={(e) => setEditEventForm({...editEventForm, primaryColor: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={editEventForm.primaryColor}
                          onChange={(e) => setEditEventForm({...editEventForm, primaryColor: e.target.value})}
                          placeholder="#0064B1"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Secondary Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editEventForm.secondaryColor}
                          onChange={(e) => setEditEventForm({...editEventForm, secondaryColor: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={editEventForm.secondaryColor}
                          onChange={(e) => setEditEventForm({...editEventForm, secondaryColor: e.target.value})}
                          placeholder="#111111"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 mt-6">
                  <Button variant="outline" onClick={() => setEditingEvent(null)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEventEdit}>
                    💾 Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditUserModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold">✏️ Edit User</h2>
                <p className="text-sm text-gray-500">Update user information</p>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input
                    value={editUserForm.name}
                    onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})}
                    placeholder="User's full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={editUserForm.email}
                    onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})}
                    placeholder="user@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Partner ID (1-2 characters)</label>
                  <Input
                    value={editUserForm.partner_id}
                    onChange={(e) => setEditUserForm({...editUserForm, partner_id: e.target.value.toUpperCase().slice(0, 2)})}
                    placeholder="e.g., R, GS, or JD"
                    maxLength={2}
                  />
                  <p className="text-xs text-gray-500 mt-1">1-2 character identifier for ticket allocations</p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 p-6 border-t bg-gray-50 rounded-b-xl">
                <Button variant="outline" onClick={() => setShowEditUserModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveUserEdit}>
                  💾 Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SuperAdminPage;
